import {useContext, useEffect, useRef, useState} from 'react';
import {Alert, Button, Stack, Typography} from '@mui/material';
import PersonAddOutlinedIcon from '@mui/icons-material/PersonAddOutlined';
import axios from 'axios';
import {parsePhoneNumber} from 'libphonenumber-js/max';
import {useTranslation} from 'react-i18next';
import {InviteSenderType} from '../../../../../backend/enums/invite-sender-type';
import {InviteType} from '../../../../../backend/enums/invite-type';
import {RelationshipDirection} from '../../../../../backend/enums/relationship-direction';
import {RoleOnRelationship} from '../../../../../backend/enums/role-on-relationship';
import {AuthContext} from '../../../../../contexts/auth/context';
import {translations} from '../../../../../i18n/translations';
import {BackendApiManager} from '../../../../../managers/backend-api';
import {User} from '../../../../../backend/interfaces/user';
import FlatInput from '../../../../../components/flat-input/flat-input';
import {FlatSelectDropdown} from '../../../../../components/flat-select-dropdown/flat-select-dropdown';
import {PhoneNumberInput} from '../../../../../components/phone-number-input/phone-number-input';
import {validateMobilePhoneNumber} from '../../../../../utils/functions/validate-mobile-phone-number';
import {Modal, ModalAction} from '../../../../../components/modal/modal';

export default function InviteCaregiver({senior}: {senior: User}) {
  const {state} = useContext(AuthContext);
  const session = state.session;
  const relationship = session?.selectedRelationship;
  const eligible =
    !!session &&
    !!relationship &&
    relationship.withUser.id === senior.id &&
    relationship.direction === RelationshipDirection.CaregiverToSenior &&
    session.relationships.some(
      (item) => item.id === relationship.id && item.withUser.id === senior.id
    );
  const context = `${session?.id}:${relationship?.id}:${senior.id}`;
  return eligible ? (
    <InviteDialog
      key={context}
      senior={senior}
      callerId={session.id}
      relationshipId={relationship.id}
    />
  ) : null;
}

function InviteDialog({
  senior,
  callerId,
  relationshipId,
}: {
  senior: User;
  callerId: string;
  relationshipId: string;
}) {
  const {t} = useTranslation();
  const {state} = useContext(AuthContext);
  const current = useRef(state.session);
  current.current = state.session;
  const alive = useRef(true);
  const pending = useRef(false);
  const [step, setStep] = useState<'closed' | 'form' | 'confirm' | 'success' | 'uncertain'>(
    'closed'
  );
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [method, setMethod] = useState<'email' | 'phone'>('email');
  const [phone, setPhone] = useState('');
  const [countryCode, setCountryCode] = useState({phone: '+351', code: 'PT'});
  const [phoneTouched, setPhoneTouched] = useState(false);
  const [error, setError] = useState('');
  const [sending, setSending] = useState(false);
  useEffect(() => {
    alive.current = true;
    return () => {
      alive.current = false;
    };
  }, []);
  const close = () => {
    if (!pending.current) {
      setStep('closed');
      setName('');
      setEmail('');
      setMethod('email');
      setPhone('');
      setCountryCode({phone: '+351', code: 'PT'});
      setPhoneTouched(false);
      setError('');
    }
  };
  const phoneInput = `${countryCode.phone}${phone.trim()}`;
  const phoneValidation = phone.trim() ? validateMobilePhoneNumber(phoneInput) : undefined;
  const validPhone = !!phoneValidation?.isValidPhoneNumber && phoneValidation.isMobilePhoneNumber;
  const valid =
    name.trim().length > 0 &&
    (method === 'email' ? /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim()) : validPhone);
  const destination =
    method === 'phone' && validPhone ? parsePhoneNumber(phoneInput).number : email.trim();
  const send = async () => {
    const session = current.current;
    const rel = session?.selectedRelationship;
    if (
      pending.current ||
      !valid ||
      !session ||
      session.id !== callerId ||
      rel?.id !== relationshipId ||
      rel.withUser.id !== senior.id ||
      rel.direction !== RelationshipDirection.CaregiverToSenior ||
      !session.relationships.some(
        (item) => item.id === relationshipId && item.withUser.id === senior.id
      )
    )
      return;
    pending.current = true;
    setSending(true);
    setError('');
    try {
      const response = await BackendApiManager.instance.post(
        '/invites',
        {
          type: InviteType.Relationship,
          senderType: InviteSenderType.Caregiver,
          senderId: callerId,
          data: {
            targetUserId: senior.id,
            destinationRole: RoleOnRelationship.Contact,
            destinationName: name.trim(),
            ...(method === 'phone'
              ? {destinationPhoneNumber: destination}
              : {destinationEmail: destination}),
          },
        },
        {retryOnBadGateway: false, timeout: 30000}
      );
      if (alive.current) setStep(response.status === 201 ? 'success' : 'uncertain');
    } catch (err) {
      if (!alive.current) return;
      const status = axios.isAxiosError(err) ? err.response?.status : undefined;
      if (status === 400 || status === 403 || status === 404) {
        setError(
          t(
            status === 400
              ? translations.family_invite.invalid
              : status === 403
                ? translations.family_invite.denied
                : translations.family_invite.unavailable
          )
        );
      } else setStep('uncertain');
    } finally {
      pending.current = false;
      if (alive.current) setSending(false);
    }
  };
  const actions: ModalAction[] = [
    {
      label: t(translations.family_profile.close),
      type: 'destructive',
      disabled: sending,
      onClick: close,
    },
  ];
  if (step === 'form') {
    actions.push({
      label: t(translations.family_invite.next),
      type: 'nondestructive',
      disabled: !valid,
      onClick: () => setStep('confirm'),
    });
  }
  if (step === 'confirm') {
    actions.push(
      {
        label: t(translations.family_invite.back),
        type: 'neutral',
        disabled: sending,
        onClick: () => {
          setError('');
          setStep('form');
        },
      },
      {
        label: t(translations.family_invite.send),
        type: 'nondestructive',
        loading: sending,
        onClick: () => void send(),
      }
    );
  }
  return (
    <>
      <Button startIcon={<PersonAddOutlinedIcon />} onClick={() => setStep('form')}>
        {t(translations.family_invite.action)}
      </Button>
      <Modal
        open={step !== 'closed'}
        onClose={close}
        title={t(translations.family_invite.action)}
        actions={actions}
        fullWidth
        maxWidth="sm"
      >
        <Stack spacing={2}>
          <Typography>
            {t(translations.family_invite.context, {name: senior.displayName || senior.name})}
          </Typography>
          {step === 'form' && (
            <>
              <FlatInput
                label={t(translations.family_invite.name)}
                value={name}
                onChange={(event) => setName(event.target.value)}
                required
                fullWidth
              />
              <FlatSelectDropdown
                label={t(translations.family_invite.contact_method)}
                value={{
                  key: method,
                  label: t(
                    method === 'email'
                      ? translations.family_invite.email
                      : translations.family_invite.phone
                  ),
                }}
                options={[
                  {key: 'email', label: t(translations.family_invite.email)},
                  {key: 'phone', label: t(translations.family_invite.phone)},
                ]}
                onChange={(option) => setMethod(option.key === 'phone' ? 'phone' : 'email')}
              />
              {method === 'email' ? (
                <FlatInput
                  label={t(translations.family_invite.email)}
                  value={email}
                  onChange={(event) => setEmail(event.target.value)}
                  type="email"
                  required
                  fullWidth
                />
              ) : (
                <PhoneNumberInput
                  label={t(translations.family_invite.phone)}
                  countryCode={countryCode}
                  phoneNumber={phone}
                  handleChangeCountryCode={setCountryCode}
                  handleChangePhoneNumber={setPhone}
                  handleBlur={() => setPhoneTouched(true)}
                  error={
                    phoneTouched && !validPhone
                      ? t(
                          translations.informal_caregiver_invite_form.errors
                            .form_validation_invalid_mobile_phone_number
                        )
                      : undefined
                  }
                  required
                />
              )}
            </>
          )}
          {step === 'confirm' && (
            <Typography>
              {t(translations.family_invite.confirm, {name: name.trim(), destination})}
            </Typography>
          )}
          {step === 'success' && (
            <Alert severity="success">{t(translations.family_invite.success)}</Alert>
          )}
          {step === 'uncertain' && (
            <Alert severity="warning">{t(translations.family_invite.uncertain)}</Alert>
          )}
          {error && <Alert severity="error">{error}</Alert>}
        </Stack>
      </Modal>
    </>
  );
}
